import { type ReactNode, useMemo, useState } from 'react'
import {
  CheckCircleIcon,
  getSubstanceText,
  HourglassMediumIcon,
  Mtds,
  type SingleGroupedTask,
  type SinglePlan,
  SubstanceTags,
  WarningIcon
} from 'prove-common'
import {
  createColumnHelper,
  flexRender,
  type SortingState,
  useTable,
  type Row
} from '@tanstack/react-table'
import { groupedTableFeatures } from 'prove-common'
import { Link } from 'react-router'
import { buildDelegationTree, DelegationNode } from '../utils/tasksTableUtils'
import { TaskActionsMenu } from '../components/task-actions-menu/TaskActionsMenu'

type TasksTableProps = {
  plan: SinglePlan
  selectedOwnerId?: string
  grouping?: string[]
}

type TaskWithMetrics = SingleGroupedTask & {
  delegationTree?: DelegationNode
  // Derived metrics based on root delegation
  delegationId?: number
  ownerId?: string
  quantity: number // planned samples (root delegation quantity)
  delegatedSum: number // sum of direct child delegation quantities
  takenSum: number // samples taken (root delegation totalCount)
}

const ANALYSIS_CELL_CLASS_NAME =
  'align-middle w-[240px] max-w-[240px] whitespace-normal break-words text-left'
const DEFAULT_CELL_CLASS_NAME = 'align-middle w-auto whitespace-nowrap'

export default function TasksTable({
  plan,
  selectedOwnerId,
  grouping
}: Readonly<TasksTableProps>) {
  const enrichedTasks: TaskWithMetrics[] = useMemo(() => {
    const findByOwnerId = (
      node: DelegationNode | undefined,
      ownerId: string
    ): DelegationNode | undefined => {
      if (!node) return undefined
      if (node.ownerId === ownerId) return node
      for (const child of node.subDelegations) {
        const found = findByOwnerId(child, ownerId)
        if (found) return found
      }
      return undefined
    }

    return (plan.tasks ?? [])
      .flatMap((task) => {
        const fullTree = buildDelegationTree(task.delegations)
        const subtree = selectedOwnerId
          ? findByOwnerId(fullTree, selectedOwnerId)
          : fullTree
        if (!subtree) return []
        const delegationId = subtree.id
        const ownerId = subtree.ownerId
        const quantity = subtree.quantity ?? 0
        const delegatedSum = subtree.samplesDelegated ?? 0
        const takenSum = subtree.totalCount ?? 0
        const enriched: TaskWithMetrics = {
          ...task,
          delegationTree: subtree,
          delegationId,
          ownerId,
          quantity,
          delegatedSum,
          takenSum
        }
        return [enriched]
      })
      .sort((a, b) => (a.delegationId ?? 0) - (b.delegationId ?? 0))
  }, [plan.tasks, selectedOwnerId])

  const columnHelper = useMemo(
    () => createColumnHelper<typeof groupedTableFeatures, TaskWithMetrics>(),
    []
  )
  const [sorting, setSorting] = useState<SortingState>([])

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor(
          (task) => task.analysisType.sampleType?.product ?? '',
          {
            id: 'produkt',
            header: 'Produkt',
            cell: (info) => info.getValue() || '-'
          }
        ),
        columnHelper.accessor(
          (task) => task.analysisType.sampleType?.matrix ?? '',
          {
            id: 'materiale',
            header: 'Prøve av',
            cell: (info) => <strong>{info.getValue() || '-'}</strong>
          }
        ),
        columnHelper.accessor(
          (task) => getSubstanceText(task.analysisType) ?? '',
          {
            id: 'analyse',
            header: 'Analyser for',
            cell: (info) => {
              if (info.row.getIsGrouped()) return info.getValue() || '-'

              return (
                <SubstanceTags
                  analysisType={info.row.original.analysisType}
                  showAllWhenFits={false}
                />
              )
            }
          }
        ),
        // 3. Sampling point type
        columnHelper.accessor((task) => task.sampntType.nameNo ?? '', {
          id: 'sted',
          header: 'Uttakssted',
          cell: (info) => info.getValue()
        }),
        // 5. Delegation — how many of planned samples have been delegated
        columnHelper.accessor('delegatedSum', {
          id: 'fordeling',
          header: 'Fordeling',
          enableSorting: false,
          cell: (info) => <DelegationCell task={info.row.original} />,
          // prettier-ignore
          aggregatedCell: (info) => <DelegationAggregatedCell subRows={info.row.subRows} /> // NOSONAR
        }),
        // 6. Progress — samples actually taken
        columnHelper.accessor('takenSum', {
          id: 'fremdrift',
          header: 'Prøver tatt',
          enableSorting: false,
          // prettier-ignore
          cell: (info) => <ProgressCell task={info.row.original} />, // NOSONAR
          // prettier-ignore
          aggregatedCell: (info) => <ProgressAggregatedCell subRows={info.row.subRows} /> // NOSONAR
        }),
        // 7. Contract/analysis approval status
        columnHelper.accessor(
          (task) => task.analysisTypeApprovalStatus ?? 'missing',
          {
            id: 'status',
            header: 'Godkjenning',
            // prettier-ignore
            cell: (info) => <ApprovalStatusCell task={info.row.original} /> // NOSONAR
          }
        ),
        // 8. Actions menu
        columnHelper.display({
          id: 'actions',
          cell: (info) => {
            if (info.row.getIsGrouped()) return null

            return (
              <Mtds.Flex data-justify='end'>
                <Link
                  id={`task-link-${info.row.original.id}`}
                  to={`/plans/${plan.id}/tasks/${info.row.original.delegationId}`}
                  aria-label='Gå til oppgave'
                  hidden
                />
                <TaskActionsMenu
                  delegationId={info.row.original.delegationId}
                  plan={plan}
                  buttonId={`task-actions-${info.row.original.id}`}
                />
              </Mtds.Flex>
            )
          }
        })
      ]),
    [columnHelper, plan]
  )

  const table = useTable({
    features: groupedTableFeatures,
    data: enrichedTasks,
    columns,
    state: {
      columnVisibility: {
        fordeling: false
      },
      grouping,
      sorting
    },
    onSortingChange: setSorting,
    autoResetAll: false
  })
  return (
    <figure>
      <Mtds.Table data-align='center' aria-label='Oppgaver' data-nowrap>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Mtds.Table.ThSortable
                  id={header.id}
                  key={header.id}
                  className={
                    header.column.id === 'analyse'
                      ? ANALYSIS_CELL_CLASS_NAME
                      : DEFAULT_CELL_CLASS_NAME
                  }
                  aria-sort={header.column.getIsSorted()}
                  onClick={
                    header.column.getCanSort()
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Mtds.Table.ThSortable>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              id={row.id}
              data-clickdelegatefor={
                row.getIsGrouped() ? undefined : `task-link-${row.original.id}`
              }
            >
              {row.getVisibleCells().map((cell) => {
                let content: ReactNode = null
                if (cell.getIsGrouped()) {
                  content = (
                    <div>
                      <Mtds.Button
                        data-variant='secondary'
                        onClick={row.getToggleExpandedHandler()}
                        aria-expanded={row.getIsExpanded()}
                        data-arrow
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}{' '}
                        ({row.subRows.length})
                      </Mtds.Button>
                    </div>
                  )
                } else if (cell.getIsAggregated()) {
                  content = flexRender(
                    cell.column.columnDef.aggregatedCell ??
                      cell.column.columnDef.cell,
                    cell.getContext()
                  )
                } else if (!cell.getIsPlaceholder()) {
                  content = flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )
                }
                return (
                  <td
                    key={cell.id}
                    className={
                      cell.column.id === 'analyse'
                        ? ANALYSIS_CELL_CLASS_NAME
                        : DEFAULT_CELL_CLASS_NAME
                    }
                  >
                    {content}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </Mtds.Table>
    </figure>
  )
}

const ApprovalStatusCell = ({ task }: { task: TaskWithMetrics }) => {
  const status = task.analysisTypeApprovalStatus
  if (status == null) return 'Ingen kontrakt'

  const meta: Record<
    NonNullable<TaskWithMetrics['analysisTypeApprovalStatus']>,
    {
      color: 'success' | 'danger' | 'warning'
      icon: ReactNode
      label: string
    }
  > = {
    approved: {
      color: 'success',
      icon: <CheckCircleIcon />,
      label: 'Godkjent'
    },
    declined: {
      color: 'danger',
      icon: <WarningIcon />,
      label: 'Avvist'
    },
    awaiting: {
      color: 'warning',
      icon: <HourglassMediumIcon />,
      label: 'Avventer'
    }
  }
  const { color, icon, label } = meta[status]
  const tooltip =
    status === 'declined'
      ? (task.approvalComment ?? 'Ingen avvisningsgrunn oppgitt.')
      : undefined

  return (
    <Mtds.Tag data-color={color} data-tooltip={tooltip}>
      {icon}
      {label}
    </Mtds.Tag>
  )
}

const DelegationCell = ({ task }: { task: TaskWithMetrics }) => {
  const delegated = task.delegatedSum
  const planned = task.quantity
  const isFullyDelegated = planned > 0 && delegated >= planned
  return (
    <Mtds.Grid data-gap={2}>
      <span
        className={isFullyDelegated ? 'text-success-base-default' : undefined}
      >
        {delegated} av {planned}
      </span>
      <Mtds.Progress value={delegated} max={planned} />
    </Mtds.Grid>
  )
}

const DelegationAggregatedCell = ({
  subRows
}: {
  subRows: Row<typeof groupedTableFeatures, TaskWithMetrics>[]
}) => {
  let delegated = 0
  let planned = 0
  for (const sub of subRows) {
    delegated += sub.original.delegatedSum
    planned += sub.original.quantity ?? 0
  }
  return (
    <Mtds.Grid data-gap={2}>
      <span>
        {delegated} av {planned}
      </span>
      <Mtds.Progress value={delegated} max={planned} />
    </Mtds.Grid>
  )
}

const ProgressCell = ({ task }: { task: TaskWithMetrics }) => (
  <Mtds.Grid data-gap={2}>
    <span>
      {task.takenSum} av {task.quantity}
    </span>
    <Mtds.Progress value={task.takenSum} max={task.quantity} />
  </Mtds.Grid>
)

const ProgressAggregatedCell = ({
  subRows
}: {
  subRows: Row<typeof groupedTableFeatures, TaskWithMetrics>[]
}) => {
  let taken = 0
  let toTake = 0
  for (const sub of subRows) {
    taken += sub.original.takenSum
    toTake += sub.original.quantity ?? 0
  }
  return (
    <Mtds.Grid data-gap={2}>
      <span>
        {taken} av {toTake}
      </span>
      <Mtds.Progress value={taken} max={toTake} />
    </Mtds.Grid>
  )
}
